/**
 * Coachmark tour engine - spotlight + popover over the real admin UI.
 *
 * Deliberately dependency-free. This build is webpack 4 + core-js 2 with BOTH
 * yarn.lock and package-lock.json checked in: modern library dists (driver.js
 * 1.x ships optional chaining) fail the old acorn parser, intro.js is AGPL,
 * and vue-tour is unmaintained. Our needs are small enough to own outright.
 *
 * Mechanics:
 * - The dim is FOUR overlay panels around the target's rect, not one overlay
 *   with a hole: the panels take pointer events (blocking stray clicks) while
 *   the gap leaves the highlighted element genuinely clickable - which is what
 *   lets "advanceOn: click" advance the tour by doing the real action.
 * - Multi-route: steps declare `route`; the engine router.pushes and then
 *   WAITS for the selector to exist (Vue renders asynchronously; the element
 *   is never there on the same tick). A step whose selector never shows up is
 *   skipped rather than trapping the user - `optional` steps wait briefly,
 *   required ones longer, but nothing blocks forever.
 * - The engine owns UI only. Persistence and gating live with the caller
 *   (src/utils/tour/index.js) via callbacks.
 *
 * ES5-friendly on purpose: no optional chaining / nullish coalescing - the
 * babel config here predates both.
 */

var Z_DIM = 9990;
var Z_POP = 9995;
var PAD = 6; // px of breathing room around the highlighted rect

var state = null; // single active tour, module-level

function el(tag, className, styles) {
  var node = document.createElement(tag);
  if (className) node.className = className;
  if (styles) {
    for (var k in styles) node.style[k] = styles[k];
  }
  return node;
}

function targetRect(target) {
  var r = target.getBoundingClientRect();
  return {
    top: Math.max(0, r.top - PAD),
    left: Math.max(0, r.left - PAD),
    right: Math.min(window.innerWidth, r.right + PAD),
    bottom: Math.min(window.innerHeight, r.bottom + PAD)
  };
}

function isRenderable(el) {
  if (!el) return false;
  if (el === document.body) return true;
  // getClientRects, not offsetParent: offsetParent is ALSO null for
  // position:fixed elements (slideout panels, dialogs), which are exactly
  // the kind of thing a tour step wants to point at. An empty rect list
  // means display:none or a detached subtree.
  if (el.getClientRects().length === 0) return false;
  // A closed v-navigation-drawer keeps layout but sets visibility:hidden -
  // rendered geometry, nothing on screen.
  return window.getComputedStyle(el).visibility !== "hidden";
}

function inNavDrawer(node) {
  return !!(node && node.closest && node.closest(".v-navigation-drawer"));
}

// A drawer sliding in is visible from the first frame of its transition, so
// an anchor inside it would be measured mid-slide and ringed in the wrong
// place. Settled = the drawer's box sits fully inside the viewport.
function drawerSettled(node) {
  var drawer = node.closest(".v-navigation-drawer");
  if (!drawer) return true;
  var r = drawer.getBoundingClientRect();
  return r.left >= -0.5 && r.right <= window.innerWidth + 0.5;
}

// Ask the layout to open or close the navigation drawer. Below Vuetify's
// 1264px mobile breakpoint the drawer is temporary and hides itself, which
// left every nav-anchored step "present but hidden" and silently skipped on
// a laptop (5 of 7 core steps at 1180px, 17 Sep 2026). Dashboard.vue listens
// and hands the request to the drawer as a prop; the hamburger is untouched.
function requestNav(open) {
  try {
    window.dispatchEvent(new CustomEvent("improve-tour-nav", { detail: { open: open } }));
  } catch (e) {
    // No CustomEvent constructor: nothing to do, the step stands down as before.
  }
}

function releaseNav() {
  if (state && state.navOpened) {
    state.navOpened = false;
    requestNav(false);
  }
}

function waitForElement(selector, timeoutMs) {
  return new Promise(function(resolve) {
    var start = Date.now();
    var hiddenSince = null;
    (function poll() {
      if (!state) return resolve(null); // tour stopped while waiting
      var found = document.querySelector(selector);
      if (isRenderable(found) && drawerSettled(found)) {
        return resolve(found);
      }
      // In the DOM but hidden inside a collapsed navigation drawer: open the
      // drawer (once per step) and give it the normal budget to slide in,
      // instead of failing fast as a hidden element.
      if (found && !state.navRequested && inNavDrawer(found)) {
        state.navRequested = true;
        state.navOpened = true;
        requestNav(true);
        hiddenSince = null;
        return setTimeout(poll, 120);
      }
      // Distinguish "not in the DOM yet" (Vue still rendering: keep the full
      // budget) from "present but hidden" (a collapsed mobile drawer will
      // never show itself): fail the latter fast instead of freezing the
      // tour for the whole timeout.
      if (found) {
        if (hiddenSince === null) hiddenSince = Date.now();
        if (Date.now() - hiddenSince > 900) return resolve(null);
      } else {
        hiddenSince = null;
      }
      if (Date.now() - start > timeoutMs) return resolve(null);
      setTimeout(poll, 120);
    })();
  });
}

function clearStepUi() {
  if (!state) return;
  state.stepEls.forEach(function(n) {
    if (n && n.parentNode) n.parentNode.removeChild(n);
  });
  state.stepEls = [];
  if (state.goneTimer) {
    clearInterval(state.goneTimer);
    state.goneTimer = null;
  }
  if (state.clickTarget) {
    state.clickTarget.removeEventListener("click", state.clickHandler);
    state.clickTarget = null;
    state.clickHandler = null;
  }
}

function renderDim(rect) {
  // Four panels around the hole. Full-screen dim when there is no target.
  var panels;
  if (rect) {
    panels = [
      { top: 0, left: 0, width: "100%", height: rect.top + "px" },
      {
        top: rect.bottom + "px",
        left: 0,
        width: "100%",
        height: Math.max(0, window.innerHeight - rect.bottom) + "px"
      },
      {
        top: rect.top + "px",
        left: 0,
        width: rect.left + "px",
        height: rect.bottom - rect.top + "px"
      },
      {
        top: rect.top + "px",
        left: rect.right + "px",
        width: Math.max(0, window.innerWidth - rect.right) + "px",
        height: rect.bottom - rect.top + "px"
      }
    ];
  } else {
    panels = [{ top: 0, left: 0, width: "100%", height: "100%" }];
  }

  panels.forEach(function(p) {
    var d = el("div", "improve-tour-dim", {
      position: "fixed",
      top: typeof p.top === "number" ? p.top + "px" : p.top,
      left: typeof p.left === "number" ? p.left + "px" : p.left,
      width: p.width,
      height: p.height,
      background: "rgba(16, 24, 32, 0.62)",
      zIndex: String(Z_DIM),
      transition: "all 0.15s ease"
    });
    document.body.appendChild(d);
    state.stepEls.push(d);
  });

  if (rect) {
    var ring = el("div", "improve-tour-ring", {
      position: "fixed",
      top: rect.top + "px",
      left: rect.left + "px",
      width: rect.right - rect.left + "px",
      height: rect.bottom - rect.top + "px",
      border: "2px solid #14b8a6",
      borderRadius: "6px",
      boxSizing: "border-box",
      pointerEvents: "none",
      zIndex: String(Z_DIM + 1)
    });
    document.body.appendChild(ring);
    state.stepEls.push(ring);
  }
}

function renderPopover(step, rect, index, total) {
  var pop = el("div", "improve-tour-popover", {
    position: "fixed",
    width: "340px",
    maxWidth: "92vw",
    background: "#ffffff",
    borderRadius: "10px",
    boxShadow: "0 12px 40px rgba(16, 24, 32, 0.28)",
    padding: "18px 20px 14px",
    zIndex: String(Z_POP),
    // The popover mounts on document.body, OUTSIDE the app root, so
    // "inherit" would inherit the browser default serif. Match the app's
    // real stack (fonts.scss); the faces are already loaded by index.html.
    fontFamily: "'Source Sans Pro', Roboto, 'Open Sans', sans-serif",
    color: "#1e2a36"
  });

  var counter = el("div", "", {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#7a8794",
    marginBottom: "6px"
  });
  counter.textContent = "Step " + (index + 1) + " of " + total;
  pop.appendChild(counter);

  var h = el("div", "", {
    fontSize: "17px",
    fontWeight: "600",
    marginBottom: "6px"
  });
  h.textContent = step.title;
  pop.appendChild(h);

  var b = el("div", "", {
    fontSize: "13.5px",
    lineHeight: "1.5",
    color: "#42505e",
    marginBottom: "14px"
  });
  b.textContent = step.body;
  pop.appendChild(b);

  var row = el("div", "", {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  });

  var skip = el("button", "improve-tour-skip", {
    fontFamily: "inherit",
    background: "none",
    border: "none",
    color: "#7a8794",
    fontSize: "12.5px",
    cursor: "pointer",
    padding: "6px 0"
  });
  skip.type = "button";
  skip.textContent = "End tour";
  skip.addEventListener("click", function() {
    stop("skipped");
  });
  row.appendChild(skip);

  var right = el("div", "", { display: "flex", gap: "8px" });

  if (index > 0) {
    var back = el("button", "improve-tour-back", {
      fontFamily: "inherit",
      background: "none",
      border: "1px solid #cfd8e0",
      borderRadius: "6px",
      color: "#42505e",
      fontSize: "13px",
      cursor: "pointer",
      padding: "7px 14px"
    });
    back.type = "button";
    back.textContent = "Back";
    back.addEventListener("click", function() {
      goTo(index - 1);
    });
    right.appendChild(back);
  }

  var next = el("button", "improve-tour-next", {
    fontFamily: "inherit",
    background: "#14b8a6",
    border: "none",
    borderRadius: "6px",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "7px 16px"
  });
  next.type = "button";
  next.textContent = index === total - 1 ? "Done" : "Next";
  next.addEventListener("click", function() {
    if (index === total - 1) {
      stop("completed");
    } else {
      goTo(index + 1);
    }
  });
  right.appendChild(next);

  row.appendChild(right);
  pop.appendChild(row);
  document.body.appendChild(pop);
  state.stepEls.push(pop);

  // Position: below the target if it fits, above otherwise, clamped to the
  // viewport. Centered card when there is no target.
  var popRect = pop.getBoundingClientRect();
  var top, left;
  if (rect) {
    var below = rect.bottom + 12;
    var fitsBelow = below + popRect.height <= window.innerHeight - 8;
    var fitsAbove = rect.top - popRect.height - 12 >= 8;
    var fitsLeft = rect.left - popRect.width - 12 >= 8;
    var fitsRight = rect.right + 12 + popRect.width <= window.innerWidth - 8;
    // A tall target (the Add User slideout is the full height of the screen)
    // has no room above or below: placed there the popover lands ON the
    // target, over the panel's own fields (seen 17 Sep 2026). Sit beside it.
    var tall = rect.bottom - rect.top > window.innerHeight * 0.6;
    if (fitsBelow && !tall) {
      top = below;
      left = Math.min(Math.max(8, rect.left), window.innerWidth - popRect.width - 8);
    } else if (fitsAbove && !tall) {
      top = rect.top - popRect.height - 12;
      left = Math.min(Math.max(8, rect.left), window.innerWidth - popRect.width - 8);
    } else if (fitsLeft || fitsRight) {
      left = fitsLeft ? rect.left - popRect.width - 12 : rect.right + 12;
      top = Math.min(Math.max(8, rect.top + 12), window.innerHeight - popRect.height - 8);
    } else {
      top = fitsBelow ? below : Math.max(8, rect.top - popRect.height - 12);
      left = Math.min(Math.max(8, rect.left), window.innerWidth - popRect.width - 8);
    }
  } else {
    top = Math.max(8, (window.innerHeight - popRect.height) / 2);
    left = Math.max(8, (window.innerWidth - popRect.width) / 2);
  }
  pop.style.top = top + "px";
  pop.style.left = left + "px";
}

function isDisabledTarget(target) {
  return !!(
    target &&
    (target.disabled ||
      (target.classList && target.classList.contains("v-btn--disabled")) ||
      target.getAttribute("disabled") !== null)
  );
}

function renderStep(target, step, index, total) {
  clearStepUi();
  var rect = target ? targetRect(target) : null;

  // A target that is laid out but off-viewport (e.g. inside a closed,
  // translated drawer) clamps to a collapsed rect. Render as a centered
  // card rather than painting a ring around nothing - and never bind an
  // advance-on-click to an element nobody can see.
  if (rect && (rect.right - rect.left <= 0 || rect.bottom - rect.top <= 0)) {
    rect = null;
    target = null;
  }

  var disabled = isDisabledTarget(target);
  var stepToRender = step;
  if (disabled && step.disabledBody) {
    stepToRender = { title: step.title, body: step.disabledBody };
  }

  renderDim(rect);
  renderPopover(stepToRender, rect, index, total);

  // Never wire advance-on-click to a disabled control: Vuetify puts
  // pointer-events:none on disabled buttons, so the click can never come
  // and the copy would be inviting an impossible action.
  if (target && step.advanceOn === "click" && !disabled) {
    state.clickTarget = target;
    state.clickHandler = function() {
      // Let the real click do its work (open a panel, navigate a tab) before
      // the tour moves on, so we never yank focus from what it triggered.
      setTimeout(function() {
        if (state && state.index === index) goTo(index + 1);
      }, 400);
    };
    target.addEventListener("click", state.clickHandler);
  }

  // A step about something transient (the Add User panel): when it goes,
  // because they saved or closed it, the tour carries on by itself instead
  // of leaving a ring around empty space with "press Next" copy.
  if (target && step.advanceOnGone) {
    state.goneTimer = setInterval(function() {
      if (!state || state.index !== index) return;
      var current = document.querySelector(step.selector);
      if (!isRenderable(current)) {
        clearInterval(state.goneTimer);
        state.goneTimer = null;
        goTo(index + 1);
      }
    }, 300);
  }
}

// A slideout panel the user left open would sit inside the hole of the next
// step on the same page and hide what the step points at (People: Add User
// then Import Users). When the tour leaves a panel step for a step that is
// not about the panel, close it through its own close button.
function closeOpenPanel() {
  var btn = document.querySelector(".slideout-panel .slideout .close, .slideout-panel .slideout .cancel-button");
  if (btn) {
    try {
      btn.click();
    } catch (e) {
      // nothing to close
    }
  }
}

function goTo(index) {
  if (!state) return;
  var chapter = state.chapter;

  if (index >= chapter.steps.length) return stop("completed");
  if (index < 0) index = 0;

  // Direction of travel, captured BEFORE state.index moves: when a step's
  // element is missing we skip onwards in the same direction, so Back over a
  // missing optional step keeps going back instead of bouncing forward.
  var dir = index >= state.index ? 1 : -1;

  state.index = index;
  if (state.onStepChange) state.onStepChange(chapter.id, index);

  // A route hop + element wait can take a second or two: clear the previous
  // step's popover immediately and hold a plain full dim, so stale copy with
  // a seemingly dead Next button never lingers over the wait.
  clearStepUi();
  renderDim(null);

  var step = chapter.steps[index];
  // A drawer this tour opened stays open only for nav steps: left open on a
  // temporary drawer, its scrim would sit inside the hole of the next step
  // and swallow the click the step invites.
  state.navRequested = false;
  if (!(step.selector && /data-tour=.nav-/.test(step.selector))) {
    releaseNav();
  }
  var previous = state.lastStep || null;
  state.lastStep = step;
  var isPanelStep = function(s) {
    return !!(s && s.selector && /slideout/.test(s.selector));
  };
  if (isPanelStep(previous) && !isPanelStep(step)) {
    closeOpenPanel();
  }
  var proceed = function() {
    if (!state || state.index !== index) return;
    // Remember the path this step actually renders on: the wander-off
    // watcher uses it for steps that declare no route of their own.
    state.stepPath = state.router.currentRoute.path;
    // Required steps get a generous wait (route + render); optional ones a
    // short one - their absence is expected, not a failure.
    if (!step.selector) {
      renderStep(null, step, index, chapter.steps.length);
      return;
    }
    waitForElement(step.selector, step.optional ? 1500 : 7000).then(function(
      target
    ) {
      if (!state || state.index !== index) return; // superseded meanwhile
      if (!target) {
        // Never trap the user on a missing element - skip onwards in the
        // direction of travel (dir captured at entry, and goTo(-1) clamps,
        // so skipping backwards off the start is safe).
        return goTo(index + dir);
      }
      try {
        target.scrollIntoView({ block: "center", behavior: "auto" });
      } catch (e) {
        // scrollIntoView options unsupported: plain call is fine
        target.scrollIntoView();
      }
      // One frame for the scroll to settle before measuring.
      requestAnimationFrame(function() {
        if (!state || state.index !== index) return;
        renderStep(target, step, index, chapter.steps.length);
      });
    });
  };

  if (step.route && state.router.currentRoute.path !== step.route) {
    state.selfNavigating = true;
    state.router
      .push(step.route)
      .catch(function() {
        /* duplicate-navigation noise */
      })
      .then(function() {
        state ? (state.selfNavigating = false) : null;
        proceed();
      });
  } else {
    proceed();
  }
}

function reposition() {
  if (!state) return;
  var step = state.chapter.steps[state.index];
  if (!step) return;
  if (step.selector) {
    var target = document.querySelector(step.selector);
    // Same acceptance test as waitForElement. Absent or hidden means a
    // pending goTo poll has not accepted this step yet - re-rendering now
    // would paint the step's UI around a target that is not there. Keep
    // whatever is currently showing and let the poll finish its job.
    if (!isRenderable(target)) return;
    renderStep(target, step, state.index, state.chapter.steps.length);
    return;
  }
  renderStep(null, step, state.index, state.chapter.steps.length);
}

function onWindowChange() {
  if (!state) return;
  if (state.rafPending) return;
  state.rafPending = true;
  requestAnimationFrame(function() {
    if (state) state.rafPending = false;
    reposition();
  });
}

/**
 * Start a chapter.
 * opts: {
 *   router        - the Vue router instance (required)
 *   startAt       - step index to resume from (default 0)
 *   onStepChange  - fn(chapterId, index)
 *   onFinish      - fn(chapterId, reason) - "completed" | "skipped" | "aborted"
 * }
 */
export function start(chapter, opts) {
  if (state) stop("aborted"); // one tour at a time

  state = {
    chapter: chapter,
    router: opts.router,
    index: 0,
    stepPath: null,
    stepEls: [],
    clickTarget: null,
    clickHandler: null,
    selfNavigating: false,
    rafPending: false,
    navRequested: false,
    navOpened: false,
    goneTimer: null,
    lastStep: null,
    onStepChange: opts.onStepChange || null,
    onFinish: opts.onFinish || null,
    unwatchRoute: null,
    keyHandler: null
  };

  // Wandering off (a nav click we didn't highlight, the browser Back button)
  // aborts gracefully rather than painting coachmarks over the wrong page.
  // Route-less steps (welcome/wrap-up cards) compare against the path they
  // actually rendered on (state.stepPath), so Back during those aborts too
  // instead of leaving a full-screen dim parked over a foreign page. The
  // SetupGuide widget lets the user resume where they left off.
  state.unwatchRoute = opts.router.afterEach(function() {
    if (!state || state.selfNavigating) return;
    var step = state.chapter.steps[state.index];
    var expected = step ? step.route || state.stepPath : null;
    if (expected && opts.router.currentRoute.path !== expected) {
      stop("aborted");
    }
  });

  state.keyHandler = function(e) {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    // An open panel or overlay owns Escape - closing the Add User slideout
    // or a Vuetify dialog must not also kill the tour. (Vuetify closes on
    // Escape without preventDefault, so presence is the load-bearing check.)
    if (
      document.querySelector(
        ".slideout-panel .slideout, .v-dialog--active, .v-menu__content--active"
      )
    ) {
      return;
    }
    // "aborted", not "skipped": Escape is a reflex, not a decision. The
    // chapter stays resumable and the core tour is not permanently
    // dismissed by a stray keypress - only the explicit End tour button
    // settles it as skipped.
    stop("aborted");
  };
  document.addEventListener("keydown", state.keyHandler);
  window.addEventListener("resize", onWindowChange);
  window.addEventListener("scroll", onWindowChange, true);

  goTo(typeof opts.startAt === "number" ? opts.startAt : 0);
}

export function stop(reason) {
  if (!state) return;
  var s = state;
  releaseNav();
  clearStepUi();
  if (s.unwatchRoute) s.unwatchRoute();
  if (s.keyHandler) document.removeEventListener("keydown", s.keyHandler);
  window.removeEventListener("resize", onWindowChange);
  window.removeEventListener("scroll", onWindowChange, true);
  var chapterId = s.chapter.id;
  var index = s.index;
  var onFinish = s.onFinish;
  state = null;
  if (onFinish) onFinish(chapterId, reason || "aborted", index);
}

export function isActive() {
  return !!state;
}

export default { start: start, stop: stop, isActive: isActive };
